/**
 * Every analytics event this page produces, in one module.
 *
 * Centralized on purpose. The event names are the *only* link between this producer
 * and the Avro schemas registered in schema-store — a typo yields silence, not an
 * error — and the `uiRegion` values have to be members of the schema's `@options`
 * set, which rejects anything it does not know and drops the event with no error.
 * A rename therefore has to be a one-file change here rather than a sweep of inlined
 * strings across call sites.
 *
 * Schemas: udemy/schema-store PR #1837, merged 2026-09-17. Registered on DEV —
 * **do not merge to prod before `esm prod register`**, or every event below is
 * dropped silently in production.
 */
import {TrackingEvent, publishEvent} from '@udemy-v2/event-tracking';

import type {CommunityListing} from './types';

/**
 * Members of the schema's `uiRegion` `@options` set that this page is allowed to
 * send. The wider set also contains `clp.badge`, `clp.banner`, `cte.badge` and
 * `cte.banner`, which belong to other producers — this page must never send them.
 *
 * The prefix (`instructor_community_landing`) deliberately differs from the route's
 * page key (`communities`): `clientHeader.page.key` and `uiRegion` are different
 * fields with different jobs, and renaming a live page key would split page-view
 * history at the deploy boundary. Leave both as they are.
 */
export const UI_REGION = {
    /**
     * One community card in the discover grid — **and** the "More about <name>" CTA on a
     * testimonial card, which shares this value (decision: Rabia, 2026-09-18).
     *
     * Read this before analysing either surface. The schema has no `@options` value for
     * the testimonial CTA, and rather than wait on a schema change the two surfaces were
     * merged onto one value. The consequence is that click-through rate per surface — the
     * join this event pair exists for — **cannot** be computed for the discover grid from
     * `uiRegion` alone: testimonial clicks land in the numerator while the denominator
     * (`PromotionPresented`) only ever counts card impressions, so card CTR reads high.
     * Splitting them later means a new `@options` value plus a one-line change here.
     */
    communityCard: 'instructor_community_landing.community_card',
    /** The discover grid's "Show more" control. */
    communityList: 'instructor_community_landing.community_list',
} as const;

/**
 * Impression and click on a community card share a field list and a `uiRegion`: the
 * pair is joined downstream on `uiRegion`, `instructor.id` and
 * `instructorCommunity.id` to give "how many people saw each card, and how many
 * clicked through to which instructor".
 */
interface CommunityPromotionData {
    /** The community owner. `long` in the schema, hence a number rather than the GraphQL `ID`. */
    instructor: {id: number};
    instructorCommunity: {id: number};
    /**
     * Always null here. The field exists for the CLP and course-taking surfaces,
     * which promote a community in the context of a course; this page has no course
     * in scope.
     */
    course: null;
    uiRegion: typeof UI_REGION.communityCard;
}

interface CommunityListExpandedData {
    uiRegion: typeof UI_REGION.communityList;
    /**
     * Communities visible to the learner once the expansion has been applied — a state
     * measurement, not a running total. The schema carries no expansion counter of its
     * own by design: "how many times did they expand" is answered by counting these
     * events at whatever grain the question wants.
     */
    itemCountAfterExpansion: number;
}

/** Fires when a community card becomes visible to a learner. */
export class InstructorCommunityPromotionPresented extends TrackingEvent<CommunityPromotionData> {
    constructor(data: CommunityPromotionData) {
        super('InstructorCommunityPromotionPresented', data);
    }
}

/** Fires when a learner selects a community card to navigate to the instructor's profile. */
export class InstructorCommunityPromotionSelected extends TrackingEvent<CommunityPromotionData> {
    constructor(data: CommunityPromotionData) {
        super('InstructorCommunityPromotionSelected', data);
    }
}

/** Fires when a learner selects "Show more" to reveal the rest of the community list. */
export class InstructorCommunityListExpanded extends TrackingEvent<CommunityListExpandedData> {
    constructor(data: CommunityListExpandedData) {
        super('InstructorCommunityListExpanded', data);
    }
}

/**
 * Parses a GraphQL `ID` into the `long` the schema wants, or null when it is not one.
 *
 * `Number()` on its own is not enough: it maps `''` to `0`, which would be published
 * as a genuine-looking id. Both ids are unsigned integers upstream, so anything that
 * is not a run of digits is treated as no id at all.
 */
function toEventId(id: string | undefined): number | null {
    if (!id || !/^\d+$/.test(id)) {
        return null;
    }

    const parsed = Number(id);

    return Number.isSafeInteger(parsed) ? parsed : null;
}

/** The pair of ids every promotion event is keyed on, as they arrive from their source. */
export interface PromotionIds {
    /** The community owner's user id. */
    instructorId?: string;
    /** The promoted community's id. */
    communityId?: string;
}

/**
 * Builds the shared payload for the impression/click pair, or returns null when the
 * promotion cannot be identified.
 *
 * For a discover-grid card both ids are non-null upstream, so the null return is a
 * guard there — it stays because an event joined to the wrong community is worse than
 * a missing one, and a silently malformed id is exactly what the schema's `long` would
 * accept without complaint. For a testimonial card it is a real path: those are
 * hardcoded editorial entries, and one without ids simply produces no event.
 */
function toPromotionData({instructorId, communityId}: PromotionIds): CommunityPromotionData | null {
    const instructor = toEventId(instructorId);
    const community = toEventId(communityId);

    if (instructor === null || community === null) {
        return null;
    }

    return {
        instructor: {id: instructor},
        instructorCommunity: {id: community},
        course: null,
        uiRegion: UI_REGION.communityCard,
    };
}

/** The ids a discover-grid listing contributes to its promotion events. */
function toListingIds(listing: CommunityListing): PromotionIds {
    return {instructorId: listing.instructor.id, communityId: listing.communityId};
}

/** Publishes the card impression. No-ops for a listing that cannot be identified. */
export function trackCommunityCardPresented(listing: CommunityListing) {
    const data = toPromotionData(toListingIds(listing));

    if (data) {
        publishEvent(new InstructorCommunityPromotionPresented(data));
    }
}

/** Publishes the card click. No-ops for a listing that cannot be identified. */
export function trackCommunityCardSelected(listing: CommunityListing) {
    const data = toPromotionData(toListingIds(listing));

    if (data) {
        publishEvent(new InstructorCommunityPromotionSelected(data));
    }
}

/**
 * Publishes the click on a testimonial card's "More about <name>" CTA, which points at
 * the instructor's profile and so promotes their community from this page.
 *
 * Same event and same `uiRegion` as a discover-grid card click — see {@link UI_REGION}
 * for what that costs the CTR join. No impression counterpart: the testimonials fire no
 * `PromotionPresented`, so this surface has click counts and no rate of its own.
 *
 * Silently no-ops for a testimonial carrying no ids, which is the state of any entry
 * whose ids have not been filled in, and of the joint testimonial that names two
 * instructors and therefore has no single one to attribute.
 */
export function trackTestimonialCtaSelected(ids: PromotionIds) {
    const data = toPromotionData(ids);

    if (data) {
        publishEvent(new InstructorCommunityPromotionSelected(data));
    }
}

/**
 * Publishes the list expansion. One event per expansion, so a learner who expands
 * three times produces three events.
 *
 * `itemCountAfterExpansion` is the size of the revealed set within the current
 * filter — viewport-independent on purpose, because the collapsed grid shows 15
 * cards on desktop and 6 on mobile through CSS alone (the route is static, so a
 * `matchMedia` check would paint the desktop count first). It understates when the
 * listing set is truncated at the gateway's page size.
 */
export function trackCommunityListExpanded(itemCountAfterExpansion: number) {
    publishEvent(
        new InstructorCommunityListExpanded({
            uiRegion: UI_REGION.communityList,
            itemCountAfterExpansion,
        }),
    );
}
